<template>
  <b-tabs
    pills
    vertical
    nav-wrapper-class="col-md-3 col-12"
    @input="resetShownDetails"
  >
    <b-tab
      v-for="member in members"
      :key="member.uuid"
      lazy
      @click="currentMember = member.uuid"
    >
      <template #title>
        <div class="d-flex align-items-center w-100">
          <b-avatar
            :src="getStorage(member.avatar)"
            :text="getInitials(member.name)"
          />
          <div class="d-flex w-100 justify-content-between">
            <p class="font-weight-bold ml-1 mb-0">
              {{ member.name }}
            </p>

            <b-badge
              variant="warning"
            >
              {{ statusesByAssignee[member.uuid]?.total || 0 }}
            </b-badge>
          </div>
        </div>
      </template>

      <b-table-simple
        hover
        borderless
        responsive
      >
        <b-th
          v-for="column in columns"
          :key="column.key"
          :class="column.tdClass"
        >
          {{ column.label }}
        </b-th>

        <b-tr v-if="!Object.values(statusesByAssignee[member.uuid] || {}).length">
          <b-td
            :colspan="columns.length"
            class="text-center text-secondary"
          >
            {{ $t('No tasks assigned') }}
          </b-td>
        </b-tr>

        <b-tr
          v-for="item, index in Object.values(statusesByAssignee[member.uuid]?.data || {})"
          :key="item.key"
          v-b-toggle="`${item.key}-tasks`"
          class="cursor-pointer"
          :class="{ 'bg-light-secondary': index % 2 === 0 }"
          @click="shownDetails[item.key] = !shownDetails[item.key]"
        >
          <b-td
            colspan="5"
            class="p-0"
          >
            <div class="d-flex align-items-center px-2 my-1">
              <feather-icon
                :icon="shownDetails[item.key] ? 'ChevronDownIcon' : 'ChevronRightIcon'"
                class="mr-2"
              />
              <span
                class="status-dot mr-1"
                :style="{ backgroundColor: item.color || '#94a3b8' }"
              />
              <span class="font-weight-bolder">{{ item.name }}</span>
              <feather-icon
                v-if="item.isFinal"
                v-b-tooltip="$t('Final Status')"
                icon="CheckCircleIcon"
                size="14"
                class="ml-50 text-success"
              />

              <b-badge
                class="ml-1"
                variant="light-warning"
              >
                {{ item.tasks?.length || 0 }}
              </b-badge>
            </div>

            <b-collapse
              :id="`${item.key}-tasks`"
              :visible="index === 0"
            >
              <b-table
                :items="item.tasks"
                :fields="columns"
                striped
                responsive
                show-empty
                class="assignee-table"
                @row-clicked="(item, _, event) => {
                  event.stopPropagation()
                  $emit('open', item)
                }"
              >
                <template #cell(id)="data">
                  <span class="ml-2">{{ data.index + 1 }}</span>
                </template>

                <template #cell(title)="data">
                  <div class="text-truncate w-32vw">
                    <span v-b-tooltip.right.hover="data.item.title">
                      {{ data.item.title }}
                    </span>
                  </div>

                  <div class="d-flex align-items-center">
                    <!-- Milestone -->
                    <div
                      v-if="data.item.milestone"
                      class="d-flex align-items-center my-50 mr-1"
                    >
                      <feather-icon
                        icon="CrosshairIcon"
                        class="mr-50"
                      />
                      <small class="text-secondary">{{ data.item.milestone }}</small>
                    </div>

                    <!-- Labels -->
                    <b-badge
                      v-for="label in data.item.labels"
                      :key="label.uuid"
                      :style="{ backgroundColor: label.color}"
                      class="mr-1"
                    >
                      {{ label.name }}
                    </b-badge>
                  </div>
                </template>

                <!-- Actions -->
                <template #cell(actions)="data">
                  <task-dropdown-actions
                    :task="data.item"
                    @edit="$emit('edit', data.item)"
                    @refresh="$emit('fetch-board')"
                  />
                </template>

                <template #cell(dueDate)="data">
                  <span v-if="data.item.dueDate">{{ FORMAT_DATE(data.item.dueDate, 'DD MMM, YYYY') }}</span>
                  <span v-else>-</span>
                </template>

                <!-- Footer -->
                <template #bottom-row>
                  <b-td
                    colspan="4"
                    class="p-0"
                  >
                    <b-button
                      variant="flat-secondary"
                      block
                      size="sm"
                      class="add-task-btn"
                      @click.stop="$emit('add-task', { id: item.id })"
                    >
                      <feather-icon
                        icon="PlusIcon"
                        size="14"
                      />
                      {{ $t('Add') }}
                    </b-button>
                  </b-td>
                </template>
              </b-table>
            </b-collapse>
          </b-td>
        </b-tr>
      </b-table-simple>
    </b-tab>
  </b-tabs>
</template>

<script>
import {
  BTabs, BTab, BAvatar, BTableSimple, BTh, BTr, BTd, BCollapse, BTable, BBadge, BButton, VBToggle, VBTooltip,
} from 'bootstrap-vue'
import { mapGetters } from 'vuex'
import TaskDropdownActions from './TaskDropdownActions.vue'

export default {
  components: {
    BTabs,
    BTab,
    BAvatar,
    BTableSimple,
    BTh,
    BTr,
    BTd,
    BCollapse,
    BTable,
    BBadge,
    BButton,
    TaskDropdownActions,
  },
  directives: {
    'b-toggle': VBToggle,
    'b-tooltip': VBTooltip,
  },
  props: {
    statuses: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      columns: [
        {
          key: 'id', thClass: 'd-none', tdClass: 'w-5 px-0',
        },
        {
          key: 'title', label: this.$t('Title'), thClass: 'd-none', tdClass: 'w-50 px-0',
        },
        {
          key: 'dueDate', label: this.$t('Due Date'), thClass: 'd-none', tdClass: 'w-25 px-0',
        },
        {
          key: 'actions', label: this.$t('Actions'), thClass: 'd-none', tdClass: 'w-10 px-0',
        },
      ],
      currentMember: null,
      shownDetails: {},
    }
  },
  computed: {
    ...mapGetters('boards', ['GET_CURRENT_BOARD']),
    members() {
      return [
        ...(this.statusesByAssignee.unassigned ? [{
          uuid: 'unassigned',
          name: this.$t('Unassigned'),
          avatar: null,
        }] : []),
        ...this.GET_CURRENT_BOARD?.members?.data,
      ] || []
    },
    statusesByAssignee() {
      return this.statuses.reduce((acc, status) => {
        const {
          id, key, name, color, isFinal,
        } = status
        if (status.tasks) {
          status.tasks.data.forEach(task => {
            const { assignee } = task
            if (assignee) {
              if (!acc[assignee.uuid]) {
                acc[assignee.uuid] = {
                  data: {
                    [status.key]: {
                      id,
                      key,
                      name,
                      color,
                      isFinal,
                      tasks: [],
                    },
                  },
                  total: 0,
                }
              } else if (!acc[assignee.uuid].data[status.key]) {
                acc[assignee.uuid].data[status.key] = {
                  id,
                  key,
                  name,
                  color,
                  isFinal,
                  tasks: [],
                }
              }

              acc[assignee.uuid].total += 1
              acc[assignee.uuid].data[status.key].tasks.push({
                uuid: task.uuid,
                title: task.title,
                dueDate: task.dueDate,
                priority: task.priority,
                milestone: task.milestone?.name,
                labels: task.labels || [],
              })
            } else {
              if (!acc.unassigned) {
                acc.unassigned = {
                  data: {
                    [status.key]: {
                      id,
                      key,
                      name,
                      color,
                      isFinal,
                      tasks: [],
                    },
                  },
                  total: 0,
                }
              } else if (!acc.unassigned.data[status.key]) {
                acc.unassigned.data[status.key] = {
                  id,
                  key,
                  name,
                  color,
                  isFinal,
                  tasks: [],
                }
              }

              acc.unassigned.total += 1
              acc.unassigned.data[status.key].tasks.push({
                uuid: task.uuid,
                title: task.title,
                dueDate: task.dueDate,
                priority: task.priority,
                milestone: task.milestone?.name,
                labels: task.labels || [],
              })
            }
          })
        }

        return acc
      }, {})
    },
  },
  mounted() {
    if (this.GET_CURRENT_BOARD) {
      this.currentMember = this.members[0]?.uuid
      this.resetShownDetails()
    }
  },
  methods: {
    resetShownDetails() {
      Object.keys(this.statusesByAssignee[this.currentMember]?.data || {}).forEach((status, index) => {
        this.$set(this.shownDetails, status, index === 0)
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@core/scss/base/pages/kanban-board.scss";

::v-deep .w-5 {
  width: 5% !important;
}

::v-deep .w-10 {
  width: 10% !important;
}

::v-deep .w-32vw {
  width: 32vw !important;
}
</style>
